import { Alert, Button, IconButton, Stack, Tooltip } from '@mui/material';
import { capitalize } from 'lodash';
import { useEffect, useState } from 'react';
import { MaintenanceIllustration } from '../../assets/illustrations';
import { FullscreenModal } from '../../components/fullscreen-modal/FullscreenModal';
import Iconify from '../../components/iconify';

const HelpLink = ({ label, value }: { label: string; value: string }) => {
  const [openVideo, setOpenVideo] = useState(false);
  const onClose = () => setOpenVideo(false);
  return (
    <>
      <Tooltip title={`Stuck? Need help with ${createLinkInfo(label).toLowerCase()}?`} describeChild>
        <Button
          id={`${label}-help-link`}
          key={label}
          variant="text"
          onClick={() => setOpenVideo(true)}
          startIcon={<Iconify icon="mdi:help-circle-outline" width={18} />}
          sx={{
            width: 'fit-content',
            // Horizontal padding for the hover/focus surface; negative margin keeps the icon
            // aligned with sibling content (checklist rows, form fields) without changing Stack gaps.
            px: 1,
            py: 0.5,
            ml: -1,
            my: 0,
            mr: 0,
            '& .MuiButton-startIcon': {
              // Iconify SVGs are inline by default and sit on the text baseline, which nudges
              // them above the label. Flex-centering the slot puts the glyph back on the mark.
              display: 'inline-flex',
              alignItems: 'center',
              marginRight: 0.5,
            },
          }}
        >
          Stuck? Need Help?
        </Button>
      </Tooltip>
      <FullscreenModal
        fullscreen
        showDialog={openVideo}
        onClose={onClose}
        aria-labelledby="help-link"
        aria-describedby={createLinkInfo(label)}
        titleText={createLinkInfo(label)}
      >
        <Stack justifyContent="center" alignItems="center" width="100%" mt={8}>
          <EmbeddedVideo link={value} />
          <div
            style={{
              position: 'fixed',
              top: 5,
              right: 5,
              width: '50px',
              height: '50px',
              zIndex: 10001, // above the iframe
              backgroundColor: 'lightgray', // or black if you want to hide it visually too
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '50%',
            }}
          >
            <IconButton onClick={onClose} aria-label="close">
              <Iconify
                icon="eva:close-outline"
                sx={{
                  width: 25,
                  height: 25,
                }}
              />
            </IconButton>
          </div>
        </Stack>
      </FullscreenModal>
    </>
  );
};

const EmbeddedVideo = ({ link }: { link: string }) => {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeFailed, setIframeFailed] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!iframeLoaded) {
        setIframeFailed(true);
      }
    }, 5000); // 5 seconds to load

    return () => clearTimeout(timer);
  }, [iframeLoaded]);

  return (
    <>
      {!iframeFailed ? (
        <iframe
          onLoad={() => setIframeLoaded(true)}
          src={link}
          allow="autoplay; fullscreen"
          title="help-link-video"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            border: 'none',
            margin: 0,
            padding: 0,
            zIndex: 9999, // Put it above most other stuff
          }}
        />
      ) : (
        <Stack justifyContent="center" alignItems="center" width="100%" height="300px" spacing={2}>
          <MaintenanceIllustration />
          <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
            Your browser doesn’t support this video. You can watch it directly{' '}
            <a href={link} target="_blank" rel="noopener noreferrer">
              here
            </a>
            .
          </Alert>
        </Stack>
      )}
    </>
  );
};

function createLinkInfo(link: string) {
  if (!link) {
    return '';
  }
  return capitalize(link.replace(/_/g, ' '));
}

export default HelpLink;
